import React from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import {
  TextB, TextItalic, ListBullets, ListNumbers, LinkSimple,
  Paragraph as PIcon, TextHOne, TextHTwo, Quotes, ArrowUUpLeft, ArrowUUpRight,
} from "@phosphor-icons/react";

const btn = "h-8 w-8 grid place-items-center rounded transition-colors text-muted-foreground hover:text-foreground hover:bg-secondary";
const btnActive = "bg-secondary text-foreground";

/**
 * Lightweight rich text editor for admin content editing.
 * Emits HTML via `onChange`. Renders read-only HTML on the display side
 * with `dangerouslySetInnerHTML` inside a `.rich-content` container styled via CSS.
 */
export default function RichTextEditor({ value, onChange, placeholder, minHeight = 120, testId }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { class: "text-emerald-700 dark:text-emerald-400 underline underline-offset-2" } }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class: "prose prose-sm max-w-none focus:outline-none px-3 py-2.5 text-[15px] leading-relaxed",
        "data-testid": testId || "rich-editor",
        style: `min-height:${minHeight}px`,
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // Treat "<p></p>" as empty
      onChange && onChange(html === "<p></p>" ? "" : html);
    },
  });

  React.useEffect(() => {
    if (!editor) return;
    const currentHtml = editor.getHTML();
    const incoming = value || "";
    if (currentHtml !== incoming && incoming !== "<p></p>") {
      editor.commands.setContent(incoming || "", false);
    }
  }, [value, editor]);

  if (!editor) return <div className="rounded-md border border-input bg-background" style={{ minHeight: minHeight + 44 }} />;

  const setLink = () => {
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("Enter URL", prev || "https://");
    if (url === null) return;
    if (url === "") editor.chain().focus().unsetLink().run();
    else editor.chain().focus().setLink({ href: url }).run();
  };

  return (
    <div className="rounded-md border border-input bg-background overflow-hidden focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
      <div className="flex items-center gap-0.5 border-b border-border p-1 bg-secondary/40 flex-wrap">
        <button type="button" onClick={() => editor.chain().focus().setParagraph().run()} className={`${btn} ${editor.isActive("paragraph") ? btnActive : ""}`} title="Paragraph" data-testid="rte-paragraph"><PIcon size={14} /></button>
        <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className={`${btn} ${editor.isActive("heading", { level: 2 }) ? btnActive : ""}`} title="Heading 2" data-testid="rte-h2"><TextHOne size={14} /></button>
        <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} className={`${btn} ${editor.isActive("heading", { level: 3 }) ? btnActive : ""}`} title="Heading 3" data-testid="rte-h3"><TextHTwo size={14} /></button>
        <div className="h-5 w-px bg-border mx-1" />
        <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} className={`${btn} ${editor.isActive("bold") ? btnActive : ""}`} title="Bold (Ctrl+B)" data-testid="rte-bold"><TextB size={14} weight="bold" /></button>
        <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} className={`${btn} ${editor.isActive("italic") ? btnActive : ""}`} title="Italic (Ctrl+I)" data-testid="rte-italic"><TextItalic size={14} /></button>
        <div className="h-5 w-px bg-border mx-1" />
        <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()} className={`${btn} ${editor.isActive("bulletList") ? btnActive : ""}`} title="Bulleted list" data-testid="rte-bullet"><ListBullets size={14} /></button>
        <button type="button" onClick={() => editor.chain().focus().toggleOrderedList().run()} className={`${btn} ${editor.isActive("orderedList") ? btnActive : ""}`} title="Numbered list" data-testid="rte-ordered"><ListNumbers size={14} /></button>
        <button type="button" onClick={() => editor.chain().focus().toggleBlockquote().run()} className={`${btn} ${editor.isActive("blockquote") ? btnActive : ""}`} title="Quote"><Quotes size={14} /></button>
        <button type="button" onClick={setLink} className={`${btn} ${editor.isActive("link") ? btnActive : ""}`} title="Link"><LinkSimple size={14} /></button>
        <div className="h-5 w-px bg-border mx-1" />
        <button type="button" onClick={() => editor.chain().focus().undo().run()} className={btn} title="Undo"><ArrowUUpLeft size={14} /></button>
        <button type="button" onClick={() => editor.chain().focus().redo().run()} className={btn} title="Redo"><ArrowUUpRight size={14} /></button>
      </div>
      <EditorContent editor={editor} />
      {placeholder && editor.isEmpty && (
        <div className="pointer-events-none px-3 -mt-[calc(1.75em+8px)] text-muted-foreground text-[15px]">{placeholder}</div>
      )}
    </div>
  );
}
